import { createElement as rc, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { TextInput, View, styled } from 'lib_ui-primitives';
import { filters } from 'lib_ui-services';
import useSearchElement from './useSearchElement';
import logging from '@sstdev/lib_logging';
import FormField from '../formElement/FormField';

const SearchField = styled(FormField).attrs({ name: 'search-element' })`
    max-width: 300px;
    min-width: 150px;
    flex-shrink: 0;
    flex-grow: 0;
`;
SearchField.displayName = 'ValueSearchField';

// A `hidden` ValueSearch stays MOUNTED (so useSearchElement still applies a loaded saved-filter value
// to the view) but is not laid out. `display: none` is cross-platform (web + RN/yoga). Used e.g. for
// the Serialized Each "Import" filter element that backs the post-import personal saved filter but
// should not appear in the Filter By pane.
const HiddenContainer = styled(View).attrs({ name: 'hidden-search-element' })`
    display: none;
`;
HiddenContainer.displayName = 'HiddenValueSearchContainer';

const TextSearch = styled(TextInput).attrs({ name: 'value-search' })`
    flex-grow: 1;
`;

/* Implemented separately from KeySearch.
 * Theoretically, KeySearch could be implemented as some combination of a SearchPane and a ValueSearch
 * With some additional logic to show/hide the input field. But that exercise will be left to the reader
 */

/**
 * @typedef {Object} Props
 * @property {Object} hNode
 * @property {string} currentRoute
 */
/** @type {import('react').FC<Props>} */
function ValueSearch(props) {
    const {
        hNode,
        hNode: { id, title, propertyPath = '', treePosition, hidden = false },
        disabled,
        filterWrapper = x => x,
        type = 'textbox'
    } = props || { hNode: {} };

    //How a selected value is translated into a filter depends on what the search element captures
    let defaultFilters = useMemo(() => filters.filterHelpers.getDefaultFilters(hNode), [hNode]);
    if (!defaultFilters?.propertiesToSearch) {
        __PRODUCTION__ &&
            logging.error(
                'Missing `propertiesToSearch` filter on `ValueSearch`. You might have to copy it up from the grid in blockly.'
            );
        defaultFilters = {
            propertiesToSearch: { propertiesToSearch: ['assetNo', 'serialNo'] },
            ...defaultFilters
        };
    }
    const valueToFilter = useCallback(
        value => {
            if (value != null && value != '') {
                const fullTextSearch = {
                    searchTerm: value
                };
                if (propertyPath) {
                    defaultFilters.propertiesToSearch.propertiesToSearch =
                        defaultFilters.propertiesToSearch.propertiesToSearch.map(v => `${propertyPath}.${v}`);
                }

                return filterWrapper(filters.fullTextSearch.getMql({ ...defaultFilters, fullTextSearch }));
            }
            return {};
        },
        [defaultFilters, propertyPath, filterWrapper]
    );

    const { value, setValue, active, onFocus, onBlur } = useSearchElement(props, valueToFilter, '');
    const fieldEmpty = !value;

    // prettier-ignore
    const field = rc(SearchField, { hNode, title, active, fieldEmpty },
        rc(TextSearch, {
            id,
            type,
            name: 'valueSearch-' + id,
            disabled,
            value,
            onChange: setValue,
            onBlur,
            onFocus,
            sequence: treePosition.sequence
        })
    );

    // Keep the element mounted (so a loaded saved filter still applies) but out of the visible pane.
    return hidden ? rc(HiddenContainer, null, field) : field;
}

ValueSearch.defaultProps = {
    disabled: false
};

ValueSearch.propTypes = {
    hNode: PropTypes.object.isRequired,
    currentRoute: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    type: PropTypes.string
};

export default ValueSearch;
